/**
 * useWhitelist - Hook para gestion de la whitelist de pruebas productivas
 * Usa TanStack Query para cache y sincronizacion con el backend.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks';
import {
  listWhitelist,
  addToWhitelist,
  removeFromWhitelist,
  searchClientsForWhitelist,
} from '@/services/credit-engine.service';
import type { AddWhitelistRequest } from '@/types/whitelist.types';
import { PARTNER_ID } from '@/constants';
import { useAuthStore } from '@/stores/auth.store';

export function useWhitelist() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [includeInactive, setIncludeInactive] = useState(false);
  // W4 authorizer no propaga claims al lambda; mandamos el email como
  // query param para poblar added_by/removed_by en la tabla whitelist.
  const userEmail = useAuthStore((s) => s.user?.email);

  const queryKey = ['policies', 'whitelist', PARTNER_ID, includeInactive] as const;

  const whitelistQuery = useQuery({
    queryKey,
    queryFn: () => listWhitelist(PARTNER_ID, includeInactive),
  });

  const addMutation = useMutation({
    mutationFn: (body: AddWhitelistRequest) => addToWhitelist(PARTNER_ID, body, userEmail),
    onSuccess: (response) => {
      void queryClient.invalidateQueries({ queryKey: ['policies', 'whitelist', PARTNER_ID] });
      const warning = response.data?.warning;
      if (warning) {
        toast.warning(`Cliente agregado con advertencia: ${warning}`);
      } else {
        toast.success('Cliente agregado a la whitelist');
      }
    },
    onError: () => {
      toast.error('Error al agregar cliente a la whitelist');
    },
  });

  const removeMutation = useMutation({
    mutationFn: (directoryId: string) => removeFromWhitelist(PARTNER_ID, directoryId, userEmail),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['policies', 'whitelist', PARTNER_ID] });
      toast.success('Cliente removido de la whitelist');
    },
    onError: () => {
      toast.error('Error al remover cliente de la whitelist');
    },
  });

  const items = whitelistQuery.data?.data ?? [];
  const activeItems = items.filter((i) => i.isActive === 'YES');

  return {
    // Datos
    items,
    activeItems,
    activeCount: activeItems.length,

    // Estado de la query
    isLoading: whitelistQuery.isLoading,
    isError: whitelistQuery.isError,
    refetch: whitelistQuery.refetch,

    // Historico toggle
    includeInactive,
    setIncludeInactive,

    // Mutaciones
    addToWhitelist: addMutation.mutate,
    removeFromWhitelist: removeMutation.mutate,
    isAdding: addMutation.isPending,
    isRemoving: removeMutation.isPending,

    // Busqueda de clientes (funcion directa, no query — debounce en el componente)
    searchClients: (q: string) => searchClientsForWhitelist(PARTNER_ID, q),
  };
}
